import * as React from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import PanesHeader from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import { useAsyncData, useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'

import attendanceSettings from 'modules/attendance/store/collections/settings'
import { UploadPictureModal } from 'modules/employees/components/FaceRecognition'
import { EmployeePictures as EmployeePicturesCollection } from 'modules/employees/store/collections/employeePictures'
import type Employee from 'modules/employees/store/models/Employee'

import EmployeePictures from './EmployeePictures'
import { EmployeePicturesProvider } from './EmployeePicturesContext'

interface Props {
  employee: Employee
}

const FaceRecognition = ({ employee }: Props) => {
  const pictures = React.useRef(new EmployeePicturesCollection()).current

  const { value: canRemovePicture } = useAsyncCan(
    'face_recognition.employee_pictures.remove',
    employee
  )

  const { value: canDownloadPicture } = useAsyncCan(
    'face_recognition.employee_pictures.download',
    employee
  )

  const { value: canReadPicture } = useAsyncCan('face_recognition.employee_pictures.read', employee)

  const { value: canUploadPicture } = useAsyncCan(
    'face_recognition.employee_pictures.add',
    employee
  )
  const { loading } = useAsyncData(
    () =>
      attendanceSettings.fetch({
        data: { employee_ids: [employee.attributes.id] },
      }),
    []
  )
  const attendanceSetting = attendanceSettings.getSettingWithinDate(moment())
  const employeeIdentifier =
    attendanceSetting?.attributes?.employee_access_search_field == 'employee_company_identifier'
      ? employee.attributes.company_identifier
      : employee.attributes.identifier

  useUnsafeAsyncData(() => {
    return pictures.fetch({
      data: {
        employee_ids: [employee.attributes.id],
      },
    })
  }, [employee])

  const [openUploadPictureModal, toggleUploadPictureModal] = useToggle(false)

  if (!canReadPicture) return null

  return (
    <PanesPadForm direction="horizontal">
      <>
        <PanesHeader
          illustration="user-bookmark"
          title={i18n.t('employee_form.face_recognition')}
          description={i18n.t('employee_form.face_recognition_info.description')}
        />
        <>
          {canUploadPicture && (
            <Button onClick={toggleUploadPictureModal(true)} type="primary" iconName="add">
              {i18n.t('employee_form.face_recognition_info.upload_picture')}
            </Button>
          )}
          {!loading && attendanceSetting && (
            <Box paddingTop="s32">
              <FormRow>
                <Text
                  readonly
                  label={i18n.t('employee_form.face_recognition_info.identifier_label')}
                  field={'employee_identifier' as any}
                  type="number"
                  placeholder={employeeIdentifier ?? undefined}
                  description={i18n.t('employee_form.face_recognition_info.identifier_description')}
                />
              </FormRow>
            </Box>
          )}
        </>
      </>
      <EmployeePicturesProvider collection={pictures}>
        <EmployeePictures
          canRemovePicture={canRemovePicture}
          canDownloadPicture={canDownloadPicture}
        />
        {openUploadPictureModal && (
          <UploadPictureModal employee={employee} onClose={toggleUploadPictureModal(false)} />
        )}
      </EmployeePicturesProvider>
    </PanesPadForm>
  )
}

export default observer(FaceRecognition)
